import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useDebouncedCallback } from 'use-debounce'
import { Formik, Form } from 'formik'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import LinearProgress from '@mui/material/LinearProgress'
import { styled } from '@mui/material/styles'

import StatField from '@/components/formik/fields/StatField'
import { useDebouncedProgress } from '@/hooks/useDebouncedProgress'

const LinearProgressContainer = styled('div')(({ theme }) => ({
    position: 'absolute',
    top: 0,
    width: '100%',
    zIndex: theme.zIndex.appBar + 1,
}))

type CommentFieldProps = {
    comment: string
    onCommentChange: (comment: string) => void
    readOnly: boolean
}

export function CommentField({ comment, onCommentChange, readOnly }: CommentFieldProps) {
    const { t } = useTranslation()
    const [isEditing, setIsEditing] = useState<boolean>(false)

    const { debouncing, setDebouncing, progress } = useDebouncedProgress()
    const debouncedUpdateComment = useDebouncedCallback((comment: string) => {
        setDebouncing(false)
        onCommentChange(comment)
        setIsEditing(false)
    }, 3000)




    return (
        <Formik
            initialValues={{ comment }}
            enableReinitialize={true}
            onSubmit={(values) => {
                onCommentChange(values.comment)
                setIsEditing(false)
                setDebouncing(false)
            }}
        >
            {({ values, setFieldValue, handleSubmit }) => (
                <Form>
                    <Box sx={{ position: 'relative' }}>
                        {debouncing && (
                            <LinearProgressContainer>
                                <LinearProgress variant="determinate" value={progress} sx={{ transform: 'rotate(180deg)' }} />
                            </LinearProgressContainer>
                        )}
                        <StatField
                            fontSize="small"
                            fullWidth
                            inputProps={{
                                onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => {
                                    setFieldValue('comment', e.target.value)
                                    setDebouncing(true)
                                    setIsEditing(true)
                                    debouncedUpdateComment(e.target.value)
                                },
                            }}
                            label={t('field.comment.label')}
                            multiline
                            name="comment"
                            readOnly={readOnly}
                            value={values.comment}
                        />
                        {isEditing && !readOnly && (
                            <Button
                                onClick={() => handleSubmit()}
                                variant="contained"
                                color="primary"
                                sx={{ mt: 1 }}
                            >
                                {t('common:button.save.label')}
                            </Button>
                        )}
                    </Box>
                </Form>
            )}
        </Formik>
    )
}

export default CommentField